import $ from 'jquery';
import MI from './math';
import MiEstimatorModal from './modal';
import applyBrandTheme from './theme';

/*
 * Issue #296: mounts the "Calculate Mortgage Insurance" button under the Mortgage Insurance field, gates it,
 * opens the estimator popup, and writes the chosen amount back.
 *
 * Page-only gating, same precedent as Save Results (save-results/index.js): the server renders
 * data-mi-estimator="true" and data-mi-factors='[...]' on the [data-render-link-calc] section, and only
 * PageTemplate.cshtml does that. The embed bundle and the homepage calculator partials never carry the
 * attributes, so initMiEstimator() returns null there and the feature does nothing -- even though they load
 * this same bundle. A missing/unparseable factor list also returns null (fail closed).
 *
 * The button is injected by JS into the field's own .panel__item rather than added to the .hbs templates, so
 * the Refinance CURRENT-loan field (name="mortgage") can never pick one up: only the input whose name is in
 * MODES[mode].mi is ever matched.
 *
 * Write-back writes MI only, in the field's own basis (yearly: mortgageins; monthly: mortgage2), then re-runs the
 * pane's own Calculate button so the result and charts update through the existing path.
 */

const MODES = {
    Mortgage: {
        kind: 'purchase',
        mi: 'mortgageins',
        basis: 'yearly',
        panes: ['advanced'],
        loan: 'loanAmount',
        term: 'loanTerm',
        required: [['loanAmount', 'gt'], ['loanTerm', 'gt'], ['interestRate', 'gt'], ['propertyTax', 'gte'], ['homeowners', 'gte']],
        clears: ['loanAmount', 'loanTerm', 'interestRate']
    },
    Purchasing: {
        kind: 'purchase',
        mi: 'mortgageins',
        basis: 'yearly',
        panes: ['advanced'],
        term: 'loanTerm',
        required: [['totalMonthlyPayment', 'gt'], ['downPayment', 'gte'], ['loanTerm', 'gt'], ['interestRate', 'gt'],
            ['propertyTax', 'gte'], ['homeowners', 'gte']],
        clears: ['totalMonthlyPayment', 'downPayment', 'loanTerm', 'interestRate', 'propertyTax', 'homeowners']
    },
    Savings: {
        kind: 'refinance',
        mi: 'mortgage2',
        basis: 'monthly',
        panes: null, // every tab
        loan: 'loanAmount',
        term: 'term',
        required: [['loanAmount', 'gt'], ['term', 'gt'], ['interestRate', 'gt'], ['propertyTaxes', 'gte'], ['homeowners', 'gte']],
        clears: ['loanAmount', 'term', 'interestRate']
    }
};

const BUTTON_LABEL = 'Calculate Mortgage Insurance';
const HELPER_DISABLED = 'Fill in the fields above to enable.';
const HELPER_ENABLED = 'Estimates MI from your loan details.';
const MOBILE_QUERY = '(max-width: 640px)';
const FLASH_MS = 1800;

let slotCounter = 0;

function readContext(calculatorRoot) {
    const $section = calculatorRoot.closest('[data-render-link-calc]');
    if (!$section.length || $section.attr('data-mi-estimator') !== 'true') return null;

    const config = MODES[calculatorRoot.attr('data-calc-mode')];
    if (!config) return null;

    let bands;
    try {
        bands = MI.normalizeBands(JSON.parse($section.attr('data-mi-factors') || '[]'));
    } catch (e) {
        return null;
    }

    return bands.length ? { config, bands } : null;
}

function field($pane, name) {
    return $pane.find('[name="' + name + '"]').first();
}

function fieldNumber($pane, name) {
    return MI.parseNumber(field($pane, name).val());
}

function requiredFieldsValid($pane, config) {
    return config.required.every(([name, rule]) => {
        const n = fieldNumber($pane, name);
        return Number.isFinite(n) && (rule === 'gt' ? n > 0 : n >= 0);
    });
}

function slotHtml(id) {
    return '<div class="mi-estimator-slot" data-mi-slot>'
        + '<button type="button" class="button-cmg button-deep-see mi-estimator-open" data-mi-open'
        + ' aria-describedby="' + id + '" disabled>' + BUTTON_LABEL + '</button>'
        + '<p class="mi-estimator-helper" id="' + id + '" data-mi-helper>' + HELPER_DISABLED + '</p>'
        + '<p class="mi-estimator-caption" data-mi-caption role="status" hidden></p>'
        + '</div>';
}

export function initMiEstimator(calculatorRoot) {
    if (!calculatorRoot || !calculatorRoot.length) return null;

    const context = readContext(calculatorRoot);
    if (!context) return null;

    const { config, bands } = context;
    const modal = new MiEstimatorModal();
    let caption = null;

    const miInputs = () => calculatorRoot.find('input[name="' + config.mi + '"]');

    function mount() {
        calculatorRoot.find('.tab-pane').each((i, pane) => {
            const $pane = $(pane);
            const paneId = ($pane.attr('id') || '').toLowerCase();
            if (config.panes && config.panes.indexOf(paneId) === -1) return;

            $pane.find('input[name="' + config.mi + '"]').each((j, input) => {
                const $item = $(input).closest('.panel__item');
                if (!$item.length || $item.closest('.panel--wrapper').find('[data-mi-slot]').length || $item.find('[data-mi-slot]').length) return;
                // Mount on its own row at the end of the field group, not inside the field's .panel__item:
                // a slot inside the item makes it taller and the group's align-items:flex-end then shifts
                // every neighbouring textbox up.
                const $group = $item.closest('.panel--wrapper');
                const $slot = $(slotHtml('mi-estimator-helper-' + (++slotCounter)));
                if ($group.length) $group.append($slot);
                else $item.append($slot);
                applyBrandTheme($slot[0]);
            });
        });
    }

    // The slot sits on its own row, so match the Mortgage Ins. textbox's width and left edge instead of
    // letting the button span more than the field it belongs to. Hidden panes measure 0 -- skip them.
    function sizeSlots() {
        calculatorRoot.find('[data-mi-slot]').each((i, slot) => {
            const $slot = $(slot);
            const $box = $slot.closest('.tab-pane').find('input[name="' + config.mi + '"]').first().closest('.panel__input');
            const width = $box.length ? $box[0].getBoundingClientRect().width : 0;
            if (!width) return;

            // Park the slot directly under the visual row that holds the Mortgage Ins. textbox: after the last
            // textbox sharing its row, so the row is never split and the button is never a row (or more) away.
            // Detached while measuring so it cannot shift the rows being compared.
            const $item = $box.closest('.panel__item');
            $slot.detach();
            const top = $item[0].getBoundingClientRect().top;
            let $last = $item;
            $item.nextAll('.panel__item').each((k, el) => {
                if (Math.abs(el.getBoundingClientRect().top - top) >= 2) return false;
                $last = $(el);
            });
            $last.after($slot);

            const left = $box[0].getBoundingClientRect().left - slot.getBoundingClientRect().left;
            slot.style.setProperty('--mi-w', Math.round(width) + 'px');
            slot.style.setProperty('--mi-x', Math.max(0, Math.round(left)) + 'px');
        });
    }

    function refresh() {
        sizeSlots();
        calculatorRoot.find('[data-mi-slot]').each((i, slot) => {
            const $slot = $(slot);
            const enabled = requiredFieldsValid($slot.closest('.tab-pane'), config);

            $slot.find('[data-mi-open]').prop('disabled', !enabled);
            $slot.find('[data-mi-helper]').text(enabled ? HELPER_ENABLED : HELPER_DISABLED);

            const $caption = $slot.find('[data-mi-caption]');
            if (caption) $caption.text(caption).prop('hidden', false);
            else $caption.text('').prop('hidden', true);
        });
    }

    function remount() {
        mount();
        refresh();
    }

    function flash() {
        const $boxes = miInputs().closest('.panel__input');
        $boxes.removeClass('mi-estimator-flash');
        void $boxes[0] && $boxes[0].offsetWidth; // restart the animation if it is already running
        $boxes.addClass('mi-estimator-flash');
        setTimeout(() => $boxes.removeClass('mi-estimator-flash'), FLASH_MS);
    }

    function writeBack($pane, result) {
        const amount = config.basis === 'yearly' ? result.annual : result.monthly;

        // MI only -- no other field is written. Every same-named input is set because the calculators keep
        // duplicated panes (Refinance has one per tab) and only reconcile them on calculate.
        miInputs().val(amount.toLocaleString('en-US'));
        caption = result.caption;
        refresh();
        flash();

        const $calc = $pane.find('[data-calculate]:visible').first();
        ($calc.length ? $calc : $pane.find('[data-calculate]').first()).trigger('click');
    }

    function open($button) {
        const $pane = $button.closest('.tab-pane');
        if (!requiredFieldsValid($pane, config)) return;

        const rate = fieldNumber($pane, 'interestRate');
        const term = fieldNumber($pane, config.term);
        const opts = {
            kind: config.kind,
            bands,
            termText: term + ' yrs',
            rateText: rate.toFixed(3).replace(/0+$/, '').replace(/\.$/, '.0') + '%',
            loanAmount: 0,
            homePrice: null,
            // Use re-renders the pane, which can detach the opener, so resolve the live button at close time.
            returnFocusTo: () => ($button[0].isConnected ? $button[0] : calculatorRoot.find('[data-mi-open]:visible')[0]),
            onUse: (result) => writeBack($pane, result),
            onClosed: (used) => {
                if (used && window.matchMedia && window.matchMedia(MOBILE_QUERY).matches) {
                    const input = miInputs().filter(':visible')[0];
                    if (input && input.scrollIntoView) input.scrollIntoView({ block: 'center', behavior: 'smooth' });
                }
            }
        };

        if (config.kind === 'refinance') {
            opts.loanAmount = fieldNumber($pane, config.loan);
        } else if (calculatorRoot.attr('data-calc-mode') === 'Purchasing') {
            // This calculator has no loan input: the loan is its output, as last displayed.
            // `totalLoan` is the headline "Your new home can cost up to $X" = loan + down payment, so it IS the home
            // price; the loan is that less the down payment. (The Advanced pane has no `LoanAmount` result element --
            // only the Basic/Amortization panes do -- so the headline is the one figure always present.)
            const headline = MI.parseNumber($pane.find('[data-result="totalLoan"]').first().text());
            const downPaid = fieldNumber($pane, 'downPayment');
            const down = Number.isFinite(downPaid) ? downPaid : 0;
            opts.homePrice = Number.isFinite(headline) && headline > down ? headline : null;
            opts.loanAmount = opts.homePrice ? opts.homePrice - down : 0;
        } else {
            // Mortgage Payment has no home price: assume 20% down, as the calculator's own fine print does.
            opts.loanAmount = fieldNumber($pane, config.loan);
            opts.homePrice = Math.round(opts.loanAmount / 0.8);
        }

        modal.open(opts);
    }

    // Delegated, so buttons mounted later need no rebinding.
    calculatorRoot.on('click', '[data-mi-open]', (e) => {
        e.preventDefault();
        open($(e.currentTarget));
    });

    // Live gating. Deferred one tick so a paste has landed in the field before it is read.
    calculatorRoot.on('input change keyup paste', '[data-input]', () => setTimeout(refresh, 0));

    // Caption clears on a real value edit (the `input` event only fires when the value changes, so Tab or an
    // arrow key does not clear it); the written MI amount itself stays. Programmatic .val() writes -- ours and
    // the calculator's own reformatting -- fire no events, so neither clears it.
    const clearing = config.clears.concat(config.mi);
    calculatorRoot.on('input', '[data-input]', (e) => {
        if (caption && clearing.indexOf($(e.target).attr('name')) !== -1) {
            caption = null;
            refresh();
        }
    });

    $(window).on('resize', sizeSlots);
    calculatorRoot.on('click', '[data-tab-link]', () => setTimeout(sizeSlots, 0));

    remount();
    return remount;
}
